import cx from 'classnames';
import {
  forwardRef,
  ComponentProps,
  ReactNode,
  useRef,
  useState,
  ChangeEvent,
  FocusEvent,
  useEffect,
  useMemo,
} from 'react';

import { useDisableFocus } from '../../../../providers/disable-focus';
import Button from '../../../button';
import Cheverlon from '../../../icons/cheverlon';
import FilterIcon from '../../../icons/filter';
import { ErrorLine } from '../errors';

import classes from './text.module.css';
import IconCloseRounded from '../../../icons/close-rounded';
import { useFormContext, useWatch } from 'react-hook-form';

type TextInputProps = {
  as?: 'input' | 'textarea';
  hideLabel?: boolean;
  label?: string;
  labelBehaviour?: 'default' | 'animated';
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  variant?: 'filter' | 'select' | 'search' | 'default';
  border?: boolean;
  error_messages?: Array<string>;
  inputExtraCss?: string;
  clearButton?: boolean;
  onClear?: () => void;
  focusBg?: boolean;
  showExtraIcon?: boolean;
  disabled?: boolean;
  scrollToTop?: boolean;
  backButtonOnFocus?: boolean;
  fullWidth?: boolean;
  inputPattern?: string;
  idToFocusOnMax?: string;
  idToFocusOnMin?: string;
  inputMaxlength?: number;
} & ComponentProps<'input'>;

const getIcon = (variant: TextInputProps['variant'], styles?: string) => {
  switch (variant) {
    case 'search':
      return <FilterIcon className={styles} />;
    case 'filter':
      return <FilterIcon className={styles} />;
    case 'select':
      return <Cheverlon className={styles} />;

    default:
      break;
  }
};

export const getVariantCSS = (
  variant: TextInputProps['labelBehaviour'],
  inputFocused?: any
) => {
  switch (variant) {
    case 'animated':
      /*To be implemented 
      if (backButtonOnFocus && inputFocused) {
        return cx(
          classes.animatedLabel,
          'absolute',
          '!left-[48px]',
          'text-neutral-80'
        );
      }*/
      return cx(classes.animatedLabel);
    case 'default':
    default:
      return cx('absolute', 'text-xs', 'font-bold', 'top-0 max-lg:top-1');
  }
};

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      as: As = 'input',
      hideLabel = false,
      labelBehaviour = 'default',
      id,
      label,
      leftIcon,
      rightIcon,
      variant = 'default',
      border = false,
      error_messages = [],
      onChange: onChangeCallback,
      onFocus: onFocusCallback,
      onBlur: onBlurCallback,
      placeholder: originalPlaceholder,
      inputExtraCss,
      clearButton,
      onClear,
      focusBg = false,
      showExtraIcon = false,
      disabled = false,
      scrollToTop = false,
      backButtonOnFocus = false,
      fullWidth = false,
      inputPattern = undefined,
      inputMaxlength = undefined,
      idToFocusOnMin = undefined,
      idToFocusOnMax = undefined,
      ...props
    },
    ref
  ) => {
    const formContext = useFormContext();
    const disableFocus = useDisableFocus();
    const innerRef = useRef<HTMLInputElement>(null);
    const labelRef = useRef<HTMLLabelElement>(null);
    const [showErrors, setShowErrors] = useState<boolean>(
      !!error_messages.length
    );
    const [isInputWithText, setIsInputWithText] = useState<boolean>(
      !!(
        (formContext &&
          props &&
          props.name &&
          formContext.getValues(props.name)) || props.value
      )
    );
    const targerRef = ref || innerRef;
    const haveText = (formContext &&
      props &&
      props.name) ? formContext.watch(props.name) : props.value;

    useEffect(() => {
      if (error_messages.length > 0) {
        setShowErrors(true);
      }
    }, [error_messages]);

    useEffect(() => {
      if (!!(
        formContext &&
        props &&
        props.name &&
        formContext.getValues(props.name)
      )) {
        setIsInputWithText(true);
      } else {
        if (props.value) {
          setIsInputWithText(true);
        } else {
          setIsInputWithText(false);
        }
      }
    }, [formContext, props, props.name]);

    const onClearButton = () => {
      if (
        (formContext &&
          props &&
          props.name &&
          formContext.getValues(props.name)) || props.value
      ) {
        if (formContext &&
          props &&
          props.name) {
          formContext.setValue(props.name, undefined);
        }
        setIsInputWithText(false);
      }

      if (onClear) {
        onClear();
      }
    };

    const customOnChange = (e: ChangeEvent<HTMLInputElement>) => {
      setIsInputWithText(true);
      if (error_messages.length) setShowErrors(false);

      if (onChangeCallback) {
        onChangeCallback(e);
      }
    };

    const customOnFocus = (e: FocusEvent<HTMLInputElement>) => {
      //targerRef.current?.focus();
      //setIsInputWithText(true);

      if (onFocusCallback) {
        onFocusCallback(e);
      }
    };

    const customOnBlur = (e: FocusEvent<HTMLInputElement>) => {
      if (e.target.value === '') {
        setIsInputWithText(false);
      }

      if (onBlurCallback) {
        onBlurCallback(e);
      }
    };

    return (
      <div
        className={cx(
          'relative',
          'w-full min-h-12 items-start',
          'flex flex-col gap-1',

        )}
      >
        <div
          className={cx(
            'relative h-12 items-end',
            {
              'w-full grid grid-cols-[1fr_48px]': clearButton && !showExtraIcon,
              'w-full grid grid-cols-[1fr_48px_48px]':
                clearButton && showExtraIcon,
            },
            {
              'w-full': fullWidth,
            }, ...(border
              ? [
                'border-b',
                showErrors && error_messages.length
                  ? 'border-red-100'
                  : disabled
                    ? 'border-neutral-50 bg-neutral-5'
                    : 'border-neutral-80 group-focus:border-brand-100 group-focus-within:border-brand-100',
              ]
              : [])
          )}
        >
          <div
            className={cx(
              'pt-5 pb-1 pl-4 max-lg:px-4',
              clearButton ? "truncate" : "px-4 max-lg:truncate"
            )}
          >
            {label && !hideLabel && (
              <label
                ref={labelRef}
                htmlFor={id}
                className={cx(
                  {
                    'group-focus-within:text-brand-100 block text-neutral-80 z-[1]': labelBehaviour !== 'animated',
                    'block text-neutral-80 z-[1] group-focus-within:text-brand-100': labelBehaviour === 'animated' && haveText,
                    'text-white absolute': labelBehaviour === 'animated' && !haveText,
                    '!text-red-100': labelBehaviour === 'animated' && haveText && showErrors && error_messages.length,
                  },
                  {
                    [classes.focus]: isInputWithText,
                  },
                  getVariantCSS(labelBehaviour),
                  {
                    'group-focus:text-neutral-50 bg-neutral-5': disabled,
                  }
                )}
                onClick={() => targerRef.current?.focus()}
              >
                {label}
              </label>
            )}
            <div
              className={cx(
                { flex: !clearButton },
                { 'group-focus-within:bg-brand-10': focusBg },
                'grow',
                classes.container
              )}
              onClick={() => {
                if (disabled) return;
                // TODO - Property 'current' does not exist on type '(instance: HTMLInputElement | null) => void'
                targerRef.current?.focus();
              }}
            >
              <As
                className={cx(
                  'flex',
                  'text-base',
                  'leading-5',
                  'min-w-full',
                  'outline-none',
                  'bg-transparent',
                  'disabled:bg-neutral-5',
                  'rounded-none',
                  {
                    'placeholder-red-100': showErrors && error_messages.length,
                  },
                  {
                    'placeholder-neutral-80 group-focus-within:placeholder-brand-100':
                      !showErrors || !error_messages.length,
                    '!text-neutral-50 !placeholder-neutral-50 !bg-neutral-5': disabled,
                  },
                  inputExtraCss,
                  classes.inputField,
                  'p-0',
                  (isInputWithText || props.value) && 'has-value'
                )}
                name={id}
                id={id}
                tabIndex={disableFocus ? -1 : 0}
                ref={targerRef}
                aria-label={label}
                disabled={disabled}
                {...props}
                onChange={customOnChange}
                onFocus={customOnFocus}
                onBlur={customOnBlur}
                placeholder={
                  originalPlaceholder || (hideLabel && label) || undefined
                }
                maxlength={inputMaxlength}
                pattern={inputPattern}
                onInput={(e) => {
                  const isMaxlength =
                    e.currentTarget.value.length.toString() ===
                    e.currentTarget.getAttribute('maxlength');
                  if (isMaxlength && idToFocusOnMax) {
                    document.getElementById(idToFocusOnMax)?.focus();
                  }
                  const isMinLength = e.currentTarget.value.length === 0;
                  if (isMinLength && idToFocusOnMin) {
                    document.getElementById(idToFocusOnMin)?.focus();
                  }
                }}
              />
            </div>
          </div>
          <div>
            {(isInputWithText || props.value) && !disabled && !props.readOnly ? (
              clearButton && (
                <Button
                  type="button"
                  onClick={onClearButton}
                  className={cx('')}
                >
                  <IconCloseRounded
                    className={cx(
                      'h-4',
                      'w-4',
                      'group-focus-within:text-brand-100',
                      {
                        'text-neutral-50 bg-neutral-5': disabled,
                      }
                    )}
                  ></IconCloseRounded>
                </Button>
              )
            ) : <>{clearButton && (
              <div className={cx('h-12 w-12')}></div>
            )}</>}
          </div>
          {showExtraIcon && (
            <div
              className={cx('flex justify-self-center self-center', {
                'text-neutral-50 bg-neutral-5': disabled,
              })}
              onClick={() => {
                // TODO - Property 'current' does not exist on type '(instance: HTMLInputElement | null) => void'
                targerRef.current?.focus();
              }}
            >
              {getIcon(
                variant,
                cx('h-4', 'w-4', 'group-focus-within:text-brand-100')
              )}
            </div>
          )}
        </div>
        {showErrors && error_messages && (
          <div className={cx('pb-2 px-4')}>
            {error_messages.map((m, k) => (
              <ErrorLine key={`${m}-${k}`} message={m} />
            ))}
          </div>
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
